import {
  createContext,
  type Dispatch,
  type MutableRefObject,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { type ApiError, Utils } from '@pbt/pbt-ui-components'

import {
  type CompletedSaleTransaction,
  type FailedSaleTransaction,
  InitiateCancelTransactionDocument,
  InitiateTerminalSaleDocument,
} from '~/api/graphql/generated/types'
import { linkUnappliedPayments } from '~/api/payments'
import {
  type PaymentData,
  type PaymentDialogContextType,
  type PaymentDialogStateType,
  PaymentFlowState,
  PaymentMethod,
} from '~/components/dashboard/invoices/payment/constants/addPaymentDialogV2Constants'
import PaymentType from '~/constants/paymentTypes'
import {
  clearPaymentStoreError,
  createManualPayment,
  createPayment,
} from '~/store/actions/payments'
import {
  getCurrentBusiness,
  getCurrentBusinessId,
  getCurrentUser,
} from '~/store/reducers/auth'
import { getPaymentMethod, getPaymentType } from '~/store/reducers/constants'
import {
  getPaymentsError,
  getPaymentsIsLoading,
} from '~/store/reducers/payments'
import type {
  InvoiceOrEstimate,
  PaymentPayload,
  UnsavedExtendPayment,
} from '~/types'
import { triggerErrorDoggo } from '~/utils/errorDoggoUtils'
import { getErrorMessage } from '~/utils/errors'
import usePrevious from '~/utils/usePrevious'

import {
  accountCreditsSelected,
  roundToTwoDecimals,
} from '../utils/clientAccountCreditUtils'
import { triggerErrorDoggoForTransactionFailureIfNeeded } from '../utils/errorDoggoUtils'
import {
  trackCancelTransactionClicked,
  trackCancelTransactionFailure,
  trackCancelTransactionSuccess,
  trackFlowError,
  trackInvoiceRefreshed,
  trackSendToTerminalClicked,
  trackTerminalFailure,
  trackTerminalSuccess,
} from '../utils/paymentDialogTrackingUtils'
import {
  checkPaymentType,
  createUpdatedPaymentData,
  extractBackendErrorMessage,
  handleCancelErrorForPartialPayment,
  refreshInvoiceAndBalanceHistory,
  resetCancelState,
} from '../utils/paymentDialogUtils'
import {
  extractFailureReasonName,
  getAccountCreditFailureMessage,
  getTransactionFailureBackendMessage,
  getTransactionFailureMessage,
  TransactionFailureReasonName,
} from '../utils/paymentErrorMapping'
import { initiateTerminalSale } from '../utils/sendToTerminalHelper'

interface HandlePaymentSuccessStateOptions {
  businessId: string
  cancelStartTimeRef: MutableRefObject<number | null>
  currentPaymentData: PaymentData
  heading?: string
  invoice: InvoiceOrEstimate
  isCancelling?: boolean
  message?: string
  originalInvoiceDue?: number
  paymentDialogDispatch: Dispatch<any>
  t: (key: string) => string
}

const handlePaymentSuccessState = ({
  currentPaymentData,
  invoice,
  paymentDialogDispatch,
  originalInvoiceDue,
  isCancelling,
  message,
  businessId,
  cancelStartTimeRef,
  t,
}: HandlePaymentSuccessStateOptions) => {
  const invoiceDue = originalInvoiceDue ?? invoice.dueToPay ?? 0
  const paymentCheck = checkPaymentType(currentPaymentData, invoiceDue)
  if (paymentCheck.isPartialPayment) {
    paymentDialogDispatch({
      type: 'RESET_FOR_PARTIAL_PAYMENT',
      payload: {
        totalPaid: paymentCheck.totalPaymentAmount,
      },
    })
    if (isCancelling && message) {
      trackCancelTransactionFailure({
        invoice,
        currentPaymentData,
        businessId,
        errorMessage: message,
        cancelStartTime: cancelStartTimeRef.current,
      })
      cancelStartTimeRef.current = null
      handleCancelErrorForPartialPayment(paymentDialogDispatch, message)
    }
  } else {
    paymentDialogDispatch({
      type: 'CLEAR_PARTIAL_PAYMENT',
    })
    if (isCancelling) {
      if (message) {
        trackCancelTransactionFailure({
          invoice,
          currentPaymentData,
          businessId,
          errorMessage: message,
          cancelStartTime: cancelStartTimeRef.current,
        })
      }
      cancelStartTimeRef.current = null
      paymentDialogDispatch({
        type: 'SET_IS_CANCELLING',
        payload: false,
      })
    }
    paymentDialogDispatch({
      type: 'SET_STATE',
      payload: PaymentFlowState.PAYMENT_SUCCESS,
    })
  }
}

type PaymentDialogAction =
  | { payload: PaymentFlowState; type: 'SET_STATE' }
  | { type: 'CANCEL_TRANSACTION' }
  | { payload: PaymentData; type: 'SET_PAYMENT_DATA' }
  | { type: 'CLEAR_PAYMENT_DATA' }
  | { type: 'RESET_DIALOG' }
  | { payload: number; type: 'SET_PARTIAL_PAYMENT' }
  | { type: 'CLEAR_PARTIAL_PAYMENT' }
  | { payload: { totalPaid: number }; type: 'RESET_FOR_PARTIAL_PAYMENT' }
  | {
      payload: {
        message: string
        severity: 'error' | 'warning'
        status: number | string
      }
      type: 'SET_PAYMENT_ERROR'
    }
  | { type: 'CLEAR_PAYMENT_ERROR' }
  | { payload: string | null; type: 'SET_CANCEL_SUCCESS_MESSAGE' }
  | {
      payload: { message: string } | null
      type: 'SET_CANCEL_ERROR'
    }
  | { payload: boolean; type: 'SET_IS_CANCELLING' }

type ActionHandlers = Record<
  PaymentDialogAction['type'],
  (
    state: PaymentDialogStateType,
    action: PaymentDialogAction,
  ) => PaymentDialogStateType
>

interface PaymentDialogProviderProps {
  children: ReactNode
  invoice: InvoiceOrEstimate
  isEstimate?: boolean
  isFromAddPaymentFlow?: boolean
  refetchInvoice?: () => void
}
const createInitialDialogState = (): PaymentDialogStateType => ({
  cancelError: null,
  cancelSuccessMessage: null,
  currentState: PaymentFlowState.RECORD_PAYMENT,
  hasPartialPayment: false,
  isCancelling: false,
  partialPaymentAmount: 0,
  paymentError: null,
  paymentData: {
    amountToCharge: '0',
    paymentMethod: PaymentMethod.CREDIT_DEBIT as string,
    paymentTerminal: '',
    authNumber: undefined,
    creditCardLastFour: undefined,
    creditCardType: undefined,
    checkNumber: undefined,
    amountReceived: undefined,
    changeDue: undefined,
    invoiceIds: [],
    accountCreditTotal: 0,
    selectedAccountCreditIds: [],
    braintreeTransactionId: '',
    rhapsodyTransactionId: '',
    paidBy: '',
  },
})

const actionHandlers: ActionHandlers = {
  SET_STATE: (state, action) => ({
    ...state,
    currentState: (action as { payload: PaymentFlowState }).payload,
  }),
  CANCEL_TRANSACTION: (state) => ({
    ...state,
    cancelError: null,
    // Keep cancelSuccessMessage so it can be displayed on RECORD_PAYMENT screen
    currentState: PaymentFlowState.RECORD_PAYMENT,
    isCancelling: false,
    paymentData: {
      ...state.paymentData,
      creditCardLastFour: undefined,
      creditCardType: undefined,
      checkNumber: undefined,
      amountReceived: undefined,
      changeDue: undefined,
      originalInvoiceDue: undefined,
      transactionId: undefined,
      externalDeviceId: undefined,
    },
  }),
  RESET_DIALOG: (state) => ({
    ...state,
    cancelError: null,
    cancelSuccessMessage: null,
    currentState: PaymentFlowState.RECORD_PAYMENT,
    hasPartialPayment: false,
    isCancelling: false,
    partialPaymentAmount: 0,
    paymentData: {
      amountToCharge: '0',
      paymentMethod: PaymentMethod.CREDIT_DEBIT as string,
      paymentTerminal: '',
      authNumber: undefined,
      creditCardLastFour: undefined,
      creditCardType: undefined,
      checkNumber: undefined,
      amountReceived: undefined,
      changeDue: undefined,
      invoiceIds: [],
      accountCreditTotal: 0,
      selectedAccountCreditIds: [],
      braintreeTransactionId: '',
      rhapsodyTransactionId: '',
      paidBy: '',
      originalInvoiceDue: undefined,
    },
    paymentError: null,
  }),
  SET_PAYMENT_DATA: (state, action) => ({
    ...state,
    paymentData: {
      ...state.paymentData,
      ...(action as { payload: PaymentData }).payload,
    },
  }),
  CLEAR_PAYMENT_DATA: (state) => ({
    ...state,
    paymentData: {
      ...createInitialDialogState().paymentData,
      creditCardLastFour: undefined,
      creditCardType: undefined,
      authNumber: undefined,
      checkNumber: undefined,
      amountReceived: undefined,
      changeDue: undefined,
      braintreeTransactionId: '',
      rhapsodyTransactionId: '',
      paidBy: '',
      originalInvoiceDue: undefined,
    },
  }),
  SET_PARTIAL_PAYMENT: (state, action) => ({
    ...state,
    hasPartialPayment: true,
    partialPaymentAmount: (action as { payload: number }).payload,
  }),
  CLEAR_PARTIAL_PAYMENT: (state) => ({
    ...state,
    hasPartialPayment: false,
    partialPaymentAmount: 0,
  }),
  RESET_FOR_PARTIAL_PAYMENT: (state, action) => {
    const { totalPaid } = (action as { payload: { totalPaid: number } }).payload
    return {
      ...state,
      currentState: PaymentFlowState.RECORD_PAYMENT,
      hasPartialPayment: true,
      partialPaymentAmount: totalPaid,
      paymentData: {
        ...createInitialDialogState().paymentData,
        originalInvoiceDue: undefined,
      },
    }
  },
  SET_PAYMENT_ERROR: (state, action) => ({
    ...state,
    paymentError: (
      action as {
        payload: {
          message: string
          severity: 'error' | 'warning'
          status: number
        }
      }
    ).payload,
  }),
  CLEAR_PAYMENT_ERROR: (state) => ({
    ...state,
    paymentError: null,
  }),
  SET_CANCEL_SUCCESS_MESSAGE: (state, action) => ({
    ...state,
    cancelSuccessMessage: (action as { payload: string | null }).payload,
    hasPartialPayment: false,
  }),
  SET_CANCEL_ERROR: (state, action) => ({
    ...state,
    cancelError: (
      action as {
        payload: { message: string } | null
      }
    ).payload,
  }),
  SET_IS_CANCELLING: (state, action) => ({
    ...state,
    isCancelling: (action as { payload: boolean }).payload,
  }),
}

export const paymentDialogReducer = (
  state: PaymentDialogStateType,
  action: PaymentDialogAction,
): PaymentDialogStateType => {
  const handler = actionHandlers[action.type]
  return handler ? handler(state, action) : state
}

const PaymentDialogContext = createContext<
  PaymentDialogContextType | undefined
>(undefined)

export const PaymentDialogProvider = ({
  children,
  invoice,
  isEstimate,
  refetchInvoice,
  isFromAddPaymentFlow = false,
}: PaymentDialogProviderProps) => {
  const [dialogState, paymentDialogDispatch] = useReducer(
    paymentDialogReducer,
    createInitialDialogState(),
  )
  const { t } = useTranslation(['Dialogs'])
  const [isButtonActionLoading, setIsButtonActionLoading] = useState(false)
  const [pendingPaymentData, setPendingPaymentData] =
    useState<PaymentData | null>(null)
  const businessId = useSelector(getCurrentBusinessId) || ''
  const PaymentTypeConstant = useSelector(getPaymentType)
  const PaymentMethodConstant = useSelector(getPaymentMethod)
  const currentUser = useSelector(getCurrentUser)
  const currentBusiness = useSelector(getCurrentBusiness)
  const dispatch = useDispatch()

  // Redux state selectors for payment operations
  const paymentError = useSelector(getPaymentsError)
  const paymentLoading = useSelector(getPaymentsIsLoading)
  const wasPaymentLoading = usePrevious(paymentLoading)

  // Track previous dueToPay to detect payments made in other tabs
  const previousDueToPayRef = useRef(invoice.dueToPay)
  // Skip the "external" partial-payment branch once after we handle our own payment
  // success, so we don't overwrite partialPaymentAmount when the invoice refetches.
  const justHandledPaymentSuccessRef = useRef(false)
  // Use ref to access latest dialogState without causing actions to be recreated
  const dialogStateRef = useRef(dialogState)
  // Flow start time for send-to-terminal; used to compute durationMs on success/failure
  const sendToTerminalStartedAtRef = useRef<number | null>(null)

  const cancelStartTimeRef = useRef<number | null>(null)

  // Update ref whenever dialogState changes
  useEffect(() => {
    dialogStateRef.current = dialogState
  }, [dialogState])

  useEffect(() => {
    if (!dialogState.isCancelling && cancelStartTimeRef.current !== null) {
      cancelStartTimeRef.current = null
    }
  }, [dialogState.isCancelling])

  // Cross-tab behavior: When the invoice updates (e.g. refetch), this effect runs.
  // We use previousDueToPayRef and flags (pendingPaymentData, paymentLoading) to tell "our" payment from another tab's:
  // our tab knows it made the update; other tabs see dueToPay drop without our flags and treat it as
  // an external partial payment, re-render with new invoice data, and may close the modal if fully paid.
  // Handles: (1) payment error, (2) our payment success (recordPayment flow), (3) external partial payment.
  useEffect(() => {
    const currentDueToPay = invoice.dueToPay || 0
    const previousDueToPay = previousDueToPayRef.current

    const hasPaymentErrorWithPending = Boolean(
      paymentError && pendingPaymentData,
    )
    const ourPaymentJustSucceeded =
      !paymentLoading &&
      wasPaymentLoading &&
      !paymentError &&
      Boolean(pendingPaymentData)
    const isExternalPartialPayment =
      previousDueToPay !== undefined &&
      currentDueToPay < previousDueToPay &&
      currentDueToPay > 0 &&
      !paymentLoading &&
      !pendingPaymentData &&
      dialogState.currentState === PaymentFlowState.RECORD_PAYMENT

    if (hasPaymentErrorWithPending) {
      const errorMessage = paymentError || ''

      triggerErrorDoggo(
        dispatch,
        errorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      paymentDialogDispatch({
        type: 'SET_PAYMENT_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })

      setIsButtonActionLoading(false)
      setPendingPaymentData(null)
    } else if (ourPaymentJustSucceeded) {
      const originalInvoiceDue =
        pendingPaymentData!.originalInvoiceDue ?? previousDueToPay
      handlePaymentSuccessState({
        currentPaymentData: pendingPaymentData!,
        invoice,
        paymentDialogDispatch,
        originalInvoiceDue,
        businessId,
        cancelStartTimeRef,
        t,
      })
      refreshInvoiceAndBalanceHistory(invoice, dispatch)
      justHandledPaymentSuccessRef.current = true
      setIsButtonActionLoading(false)
      setPendingPaymentData(null)
    } else if (
      isExternalPartialPayment &&
      !justHandledPaymentSuccessRef.current
    ) {
      paymentDialogDispatch({
        type: 'RESET_FOR_PARTIAL_PAYMENT',
        payload: {
          totalPaid: previousDueToPay - currentDueToPay,
        },
      })
    } else if (
      isExternalPartialPayment &&
      justHandledPaymentSuccessRef.current
    ) {
      justHandledPaymentSuccessRef.current = false
    }

    // Update ref for next comparison
    previousDueToPayRef.current = currentDueToPay
  }, [
    paymentError,
    paymentLoading,
    wasPaymentLoading,
    pendingPaymentData,
    invoice,
    paymentDialogDispatch,
    dialogState.currentState,
    dispatch,
  ])
  const PaymentMethodsToIdsMap = useMemo(
    () => ({
      [PaymentMethod.CASH.toString()]: `${Utils.findConstantIdByName('Cash', PaymentMethodConstant)}`,
      [PaymentMethod.CREDIT_DEBIT.toString()]: `${Utils.findConstantIdByName('Credit card', PaymentMethodConstant)}`,
      [PaymentMethod.CHECK.toString()]: `${Utils.findConstantIdByName('Check', PaymentMethodConstant)}`,
      [PaymentMethod.CARECREDIT.toString()]: `${Utils.findConstantIdByName('CareCredit', PaymentMethodConstant)}`,
    }),
    [PaymentMethodConstant],
  )

  const [initiateTerminalSaleMutation, { error }] = useMutation(
    InitiateTerminalSaleDocument,
  )
  const [initiateCancelTransactionMutation] = useMutation(
    InitiateCancelTransactionDocument,
  )

  const handleCancelSuccess = (withAccountCredits: boolean) => {
    const cancelSuccessMessage = withAccountCredits
      ? t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_SUCCESS_MESSAGE_WITH_CREDITS')
      : t(
          'Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_SUCCESS_MESSAGE_WITHOUT_CREDITS',
        )
    paymentDialogDispatch({
      type: 'SET_CANCEL_SUCCESS_MESSAGE',
      payload: cancelSuccessMessage,
    })
    paymentDialogDispatch({
      type: 'CLEAR_PAYMENT_ERROR',
    })
    resetCancelState(paymentDialogDispatch)
    if (withAccountCredits) {
      refreshInvoiceAndBalanceHistory(invoice, dispatch)
    }
  }

  const actions = useMemo(
    () => ({
      sendToTerminal: async (currentPaymentData: PaymentData) => {
        const originalInvoiceDue = invoice.dueToPay || 0
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_ERROR' })
        paymentDialogDispatch({
          type: 'SET_CANCEL_SUCCESS_MESSAGE',
          payload: null,
        })
        paymentDialogDispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })
        paymentDialogDispatch({ type: 'CLEAR_PARTIAL_PAYMENT' })
        dispatch(clearPaymentStoreError())
        setIsButtonActionLoading(true)

        const paymentDataWithOriginalDue = {
          ...currentPaymentData,
          originalInvoiceDue,
        }

        if (
          accountCreditsSelected(
            currentPaymentData.accountCreditTotal,
            currentPaymentData.selectedAccountCreditIds,
          )
        ) {
          try {
            await linkUnappliedPayments(businessId, {
              invoiceIds: [invoice.id],
              paymentIds: currentPaymentData.selectedAccountCreditIds,
            })
          } catch (error) {
            const backendErrorMessage = getErrorMessage(error as ApiError)
            const errorMessage =
              getAccountCreditFailureMessage(backendErrorMessage)
            trackFlowError({
              invoice,
              businessId,
              errorSource: 'account_credit_link',
              errorMessage: backendErrorMessage,
              paymentMethod: currentPaymentData.paymentMethod,
            })
            triggerErrorDoggo(
              dispatch,
              backendErrorMessage,
              500,
              currentUser,
              currentBusiness,
            )
            paymentDialogDispatch({
              type: 'SET_PAYMENT_ERROR',
              payload: {
                message: errorMessage,
                severity: 'error',
                status: 500,
              },
            })
            setIsButtonActionLoading(false)
            return
          }
        }

        sendToTerminalStartedAtRef.current = Date.now()
        trackSendToTerminalClicked({
          invoice,
          currentPaymentData,
          businessId,
        })
        await initiateTerminalSale(paymentDataWithOriginalDue, {
          paymentDialogDispatch,
          setIsButtonActionLoading,
          invoice,
          businessId,
          flowStartedAt: sendToTerminalStartedAtRef.current ?? undefined,
          initiateTerminalSaleMutation,
          error,
          accountCreditsApplied: accountCreditsSelected(
            currentPaymentData.accountCreditTotal,
            currentPaymentData.selectedAccountCreditIds,
          ), // selected account credits were applied at this point
          dispatch,
          currentUser,
          currentBusiness,
        })
      },
      recordPayment: async (currentPaymentData: PaymentData) => {
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_ERROR' })
        paymentDialogDispatch({
          type: 'SET_CANCEL_SUCCESS_MESSAGE',
          payload: null,
        })
        paymentDialogDispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })
        paymentDialogDispatch({ type: 'CLEAR_PARTIAL_PAYMENT' })
        dispatch(clearPaymentStoreError())
        setIsButtonActionLoading(true)

        const originalInvoiceDue = invoice.dueToPay

        // If account credits applied are equal to or greater than the remaining balance, then we don't need to create a manual payment
        if (
          roundToTwoDecimals(currentPaymentData.accountCreditTotal || 0) >=
            roundToTwoDecimals(invoice.dueToPay || 0) &&
          currentPaymentData.selectedAccountCreditIds &&
          currentPaymentData.selectedAccountCreditIds.length > 0
        ) {
          // For multiple invoices, extract individual IDs from the combined ID
          const invoiceIdsForLinking = invoice.id.split(',')

          try {
            await linkUnappliedPayments(businessId, {
              invoiceIds: invoiceIdsForLinking,
              paymentIds: currentPaymentData.selectedAccountCreditIds,
            })
          } catch (error) {
            const backendErrorMessage = getErrorMessage(error as ApiError)
            const errorMessage =
              getAccountCreditFailureMessage(backendErrorMessage)
            triggerErrorDoggo(
              dispatch,
              backendErrorMessage,
              500,
              currentUser,
              currentBusiness,
            )
            paymentDialogDispatch({
              type: 'SET_PAYMENT_ERROR',
              payload: {
                message: errorMessage,
                severity: 'error',
                status: 500,
              },
            })
            setIsButtonActionLoading(false)
            return
          }
          paymentDialogDispatch({
            type: 'CLEAR_PARTIAL_PAYMENT',
          })
          paymentDialogDispatch({
            type: 'SET_STATE',
            payload: PaymentFlowState.PAYMENT_SUCCESS,
          })
          refreshInvoiceAndBalanceHistory(invoice, dispatch)
          setIsButtonActionLoading(false)
          return
        }

        const { clientId } = invoice

        const paymentDataWithOriginalDue = {
          ...currentPaymentData,
          originalInvoiceDue,
        }

        if (isEstimate) {
          const estimatePayment: Partial<UnsavedExtendPayment> = {
            amount: parseFloat(currentPaymentData.amountToCharge || '0'),
            invoiceId: invoice.id,
            invoiceIds: [],
            notes:
              currentPaymentData.paymentMethod === PaymentMethod.CHECK
                ? currentPaymentData.checkNumber || ''
                : currentPaymentData.paymentMethod === PaymentMethod.CARECREDIT
                  ? currentPaymentData.authNumber || ''
                  : '',
            paidByPersonId: invoice.clientId ?? undefined,
            paymentCardTypeId: null,
            paymentMethodId:
              PaymentMethodsToIdsMap[currentPaymentData.paymentMethod],
            paymentTypeId: Utils.findConstantIdByName(
              PaymentType.DEPOSIT,
              PaymentTypeConstant,
            ),
            cashReceived:
              currentPaymentData.paymentMethod === PaymentMethod.CASH
                ? parseFloat(currentPaymentData.amountReceived || '0')
                : undefined,
            cashChangeGiven:
              currentPaymentData.paymentMethod === PaymentMethod.CASH
                ? parseFloat(currentPaymentData.changeDue || '0')
                : undefined,
            clientId: clientId || '',
            businessId,
          }

          setPendingPaymentData(paymentDataWithOriginalDue)
          dispatch(createPayment(clientId, estimatePayment))
        } else {
          // For multiple invoices, extract individual IDs from the combined ID
          const invoiceIds = invoice.id.split(',')

          const paymentPayload: PaymentPayload = {
            aggregatedInvoiceTotal: invoice.dueToPay,
            aggregatedPaymentTotal: currentPaymentData.accountCreditTotal || 0,
            invoiceIds,
            paymentIds: currentPaymentData.selectedAccountCreditIds || [],
            requestedPayment: {
              amount: parseFloat(currentPaymentData.amountToCharge || '0'),
              paymentMethodId:
                PaymentMethodsToIdsMap[currentPaymentData.paymentMethod],
              paymentTypeId: Utils.findConstantIdByName(
                PaymentType.PAYMENT,
                PaymentTypeConstant,
              ),
              paidByPersonId: invoice.clientId ?? undefined,
              notes:
                currentPaymentData.paymentMethod === PaymentMethod.CHECK
                  ? currentPaymentData.checkNumber
                  : currentPaymentData.paymentMethod ===
                      PaymentMethod.CARECREDIT
                    ? currentPaymentData.authNumber
                    : '',
              cashReceived:
                currentPaymentData.paymentMethod === PaymentMethod.CASH
                  ? parseFloat(currentPaymentData.amountReceived || '0')
                  : undefined,
              cashChangeGiven:
                currentPaymentData.paymentMethod === PaymentMethod.CASH
                  ? parseFloat(currentPaymentData.changeDue || '0')
                  : undefined,
            },
          }

          setPendingPaymentData(paymentDataWithOriginalDue)
          dispatch(createManualPayment(clientId, paymentPayload))
        }
      },
      handleTransactionSuccess: (
        currentPaymentData: PaymentData,
        transaction: CompletedSaleTransaction,
      ) => {
        const { isCancelling } = dialogStateRef.current
        const updatedPaymentData = createUpdatedPaymentData(
          currentPaymentData,
          transaction,
        )
        const flowStartedAt = sendToTerminalStartedAtRef.current
        const durationMs =
          flowStartedAt != null ? Date.now() - flowStartedAt : undefined
        sendToTerminalStartedAtRef.current = null
        trackTerminalSuccess({
          invoice,
          currentPaymentData,
          transaction,
          businessId,
          durationMs,
        })
        paymentDialogDispatch({
          type: 'SET_PAYMENT_DATA',
          payload: updatedPaymentData,
        })
        const cancelErrorMessage = t(
          'Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_ERROR',
        )
        handlePaymentSuccessState({
          currentPaymentData: updatedPaymentData,
          invoice,
          paymentDialogDispatch,
          originalInvoiceDue: currentPaymentData.originalInvoiceDue,
          isCancelling,
          message: isCancelling ? cancelErrorMessage : undefined,
          businessId,
          cancelStartTimeRef,
          t,
        })
        justHandledPaymentSuccessRef.current = true
        trackInvoiceRefreshed({
          invoice,
          businessId,
          trigger: 'payment_success',
        })
        refreshInvoiceAndBalanceHistory(invoice, dispatch)
      },
      handleTransactionFailure: (
        transaction: FailedSaleTransaction,
        withAccountCredits: boolean,
      ) => {
        const { isCancelling } = dialogStateRef.current
        const failureReasonName = extractFailureReasonName(transaction)
        const isCancelledByUser =
          failureReasonName === TransactionFailureReasonName.CANCELLED_BY_API
        const isCancelledByDevice =
          failureReasonName === TransactionFailureReasonName.CANCELLED_BY_DEVICE
        if ((isCancelling && isCancelledByUser) || isCancelledByDevice) {
          trackCancelTransactionSuccess({
            invoice,
            currentPaymentData: dialogStateRef.current.paymentData,
            businessId,
            cancelStartTime: cancelStartTimeRef.current,
          })
          cancelStartTimeRef.current = null
          handleCancelSuccess(withAccountCredits)
          return
        }

        resetCancelState(paymentDialogDispatch)

        const flowStartedAt = sendToTerminalStartedAtRef.current
        const durationMs =
          flowStartedAt != null ? Date.now() - flowStartedAt : undefined
        sendToTerminalStartedAtRef.current = null
        trackTerminalFailure({
          invoice,
          businessId,
          hardFailure: false,
          failureReason: failureReasonName,
          failureReasonId: transaction.error?.failureReason?.id,
          transactionId: transaction.id,
          amount: transaction.amount,
          externalDeviceId: transaction.externalDeviceId,
          paymentMethod: dialogStateRef.current.paymentData.paymentMethod,
          withAccountCredits,
          durationMs,
        })
        const errorMessage = getTransactionFailureMessage(
          transaction,
          withAccountCredits,
        )
        triggerErrorDoggoForTransactionFailureIfNeeded({
          failureReasonName,
          dispatch,
          backendErrorMessage: getTransactionFailureBackendMessage(transaction),
          fallbackMessage: errorMessage,
          currentUser,
          currentBusiness,
        })
        paymentDialogDispatch({
          type: 'SET_PAYMENT_ERROR',
          payload: {
            message: errorMessage,
            severity: 'error',
            status: 500,
          },
        })
        if (withAccountCredits) {
          trackInvoiceRefreshed({
            invoice,
            businessId,
            trigger: 'transaction_failure',
          })
          refreshInvoiceAndBalanceHistory(invoice, dispatch)
        }
      },
      handleTransactionHardFailure: (
        withAccountCredits: boolean,
        error?: any,
      ) => {
        paymentDialogDispatch({
          type: 'CANCEL_TRANSACTION',
        })
        const errorMessage = withAccountCredits
          ? t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_HARD_WITH_CREDITS',
            )
          : t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_HARD',
            )
        const backendErrorMessage = extractBackendErrorMessage(
          error,
          errorMessage,
        )

        const flowStartedAt = sendToTerminalStartedAtRef.current
        const durationMs =
          flowStartedAt != null ? Date.now() - flowStartedAt : undefined
        sendToTerminalStartedAtRef.current = null
        const { paymentData } = dialogStateRef.current
        trackTerminalFailure({
          invoice,
          businessId,
          hardFailure: true,
          paymentMethod: paymentData.paymentMethod,
          withAccountCredits,
          errorMessage: backendErrorMessage,
          amount: paymentData.amountToCharge
            ? parseFloat(paymentData.amountToCharge)
            : undefined,
          externalDeviceId: paymentData.paymentTerminal || undefined,
          transactionId: paymentData.transactionId,
          durationMs,
        })

        triggerErrorDoggo(
          dispatch,
          backendErrorMessage,
          500,
          currentUser,
          currentBusiness,
        )
        paymentDialogDispatch({
          type: 'SET_PAYMENT_ERROR',
          payload: {
            message: errorMessage,
            severity: 'error',
            status: 500,
          },
        })
        if (withAccountCredits) {
          trackInvoiceRefreshed({
            invoice,
            businessId,
            trigger: 'transaction_hard_failure',
          })
          refreshInvoiceAndBalanceHistory(invoice, dispatch)
        }
      },
      cancelTransaction: async () => {
        const { paymentData } = dialogStateRef.current
        const { externalDeviceId, transactionId } = paymentData

        if (!externalDeviceId || !transactionId) {
          return
        }

        cancelStartTimeRef.current = Date.now()

        trackCancelTransactionClicked({
          invoice,
          currentPaymentData: paymentData,
          businessId,
          cancelStartTime: cancelStartTimeRef.current,
        })

        paymentDialogDispatch({
          type: 'SET_IS_CANCELLING',
          payload: true,
        })
        setIsButtonActionLoading(true)
        paymentDialogDispatch({
          type: 'SET_CANCEL_ERROR',
          payload: null,
        })

        try {
          await initiateCancelTransactionMutation({
            variables: {
              input: {
                externalDeviceId,
                transactionId,
              },
            },
          })
        } catch (error) {
          trackCancelTransactionFailure({
            invoice,
            currentPaymentData: paymentData,
            businessId,
            errorMessage: getErrorMessage(error as ApiError),
            cancelStartTime: cancelStartTimeRef.current,
          })
          cancelStartTimeRef.current = null
          triggerErrorDoggo(
            dispatch,
            getErrorMessage(error as ApiError),
            500,
            currentUser,
            currentBusiness,
          )
          paymentDialogDispatch({
            type: 'SET_CANCEL_ERROR',
            payload: {
              message: t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_ERROR'),
            },
          })
          paymentDialogDispatch({
            type: 'SET_IS_CANCELLING',
            payload: false,
          })
        } finally {
          setIsButtonActionLoading(false)
        }
      },
      resetDialog: () => {
        setIsButtonActionLoading(false)
        cancelStartTimeRef.current = null
        paymentDialogDispatch({
          type: 'RESET_DIALOG',
        })
      },
      setPaymentData: (data: PaymentData) =>
        paymentDialogDispatch({ type: 'SET_PAYMENT_DATA', payload: data }),
      clearPaymentData: () =>
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_DATA' }),
      setPartialPayment: (amount: number) =>
        paymentDialogDispatch({ type: 'SET_PARTIAL_PAYMENT', payload: amount }),
      clearPartialPayment: () =>
        paymentDialogDispatch({ type: 'CLEAR_PARTIAL_PAYMENT' }),
      setPaymentError: (error: {
        message: string
        severity: 'error' | 'warning'
        status: number
      }) =>
        paymentDialogDispatch({ type: 'SET_PAYMENT_ERROR', payload: error }),
      clearPaymentError: () => {
        paymentDialogDispatch({ type: 'CLEAR_PAYMENT_ERROR' })
        dispatch(clearPaymentStoreError())
      },
    }),
    [
      businessId,
      invoice.clientId,
      invoice.id,
      invoice.dueToPay,
      dispatch,
      PaymentMethodConstant,
      PaymentTypeConstant,
      isEstimate,
      refetchInvoice,
      paymentDialogDispatch,
      setIsButtonActionLoading,
      initiateTerminalSaleMutation,
      initiateCancelTransactionMutation,
      error,
      currentUser,
      currentBusiness,
      t,
      invoice,
    ],
  )

  const contextValue = useMemo(
    () => ({
      dialogState,
      invoice,
      actions,
      isButtonActionLoading,
      businessId,
      isEstimate,
      isFromAddPaymentFlow,
    }),
    [
      dialogState,
      invoice,
      actions,
      isButtonActionLoading,
      businessId,
      isEstimate,
      isFromAddPaymentFlow,
    ],
  )

  return (
    <PaymentDialogContext.Provider value={contextValue}>
      {children}
    </PaymentDialogContext.Provider>
  )
}

export const usePaymentDialog = (): PaymentDialogContextType => {
  const context = useContext(PaymentDialogContext)
  if (!context) {
    throw new Error(
      'usePaymentDialog must be used within a PaymentDialogProvider',
    )
  }
  return context
}
